import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addEvent, listEvents, updateEvent, deleteEvent } from '../services/eventsService';
import { listAllLayouts, getUserLayouts, getLayoutPublic, listLayoutsByEventAdmin } from '../services/layoutsService';
import { getUserProfile } from '../services/profilesService';
import { useAuth } from '../context/AuthContext';
import { listProfilesByType } from '../services/profilesService';
import './ProfilePage.css';

const CONSENT_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';
const CONSENT_FONT_SIZE = '14px';
const CONSENT_LINE_HEIGHT = '1.6';
const CONSENT_FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px', '20px'];
const EVENT_FORM_FLAG_OPTIONS = ['required', 'optional', 'off'];

const normalizeConfiguredValue = (value) => {
  const normalized = String(value || '').trim();
  return normalized.toLowerCase() === 'na' ? '' : normalized;
};

const normalizeEventFormFlag = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (EVENT_FORM_FLAG_OPTIONS.includes(normalized)) return normalized;
  return 'required';
};

const stripFontStyleDirectives = (html = '') => {
  if (!html) return '';
  return html.replace(/style\s*=\s*"([^"]*)"/gi, (_full, styleValue) => {
    const nextStyle = styleValue
      .replace(/font-family\s*:[^;]+;?/gi, '')
      .replace(/font-size\s*:[^;]+;?/gi, '')
      .trim();
    return nextStyle ? `style="${nextStyle}"` : '';
  });
};

const normalizeConsentMessageHtml = (html = '', fontSize = CONSENT_FONT_SIZE) => {
  const cleaned = stripFontStyleDirectives(html || '').trim();
  if (!cleaned) return '';
  return `<div style="font-family:${CONSENT_FONT_FAMILY};font-size:${fontSize};line-height:${CONSENT_LINE_HEIGHT};">${cleaned}</div>`;
};

const RichTextEditor = ({ value, onChange, placeholder, fontSize = CONSENT_FONT_SIZE, onFontSizeChange }) => {
  const editorRef = React.useRef(null);

  const handleToolbarMouseDown = (e) => {
    // Keep focus/selection inside contentEditable so execCommand can apply to selection.
    e.preventDefault();
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const runCommand = (command, commandValue = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current.innerHTML || '');
  };

  const changeFontSize = (direction) => {
    const currentIndex = Math.max(0, CONSENT_FONT_SIZE_OPTIONS.indexOf(fontSize));
    const nextIndex = direction === 'up'
      ? Math.min(CONSENT_FONT_SIZE_OPTIONS.length - 1, currentIndex + 1)
      : Math.max(0, currentIndex - 1);
    if (onFontSizeChange) onFontSizeChange(CONSENT_FONT_SIZE_OPTIONS[nextIndex]);
  };

  return (
    <div className="consent-rich-editor" style={{ border: '1px solid #d0d7de', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 6, padding: 8, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('bold')}>Bold</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('italic')}>Italic</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('underline')}>Underline</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('insertUnorderedList')}>Bullets</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('outdent')}>Tab Out</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => runCommand('indent')}>Tab In</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => changeFontSize('down')}>A-</button>
        <button type="button" className="cancel-btn" onMouseDown={handleToolbarMouseDown} onClick={() => changeFontSize('up')}>A+</button>
        <span style={{ alignSelf: 'center', fontSize: 12, color: '#555' }}>Size: {fontSize}</span>
      </div>
      <div
        className="consent-rich-editor-content"
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
        data-placeholder={placeholder || ''}
        style={{ minHeight: 170, padding: 10, outline: 'none', whiteSpace: 'pre-wrap', fontFamily: CONSENT_FONT_FAMILY, fontSize, lineHeight: CONSENT_LINE_HEIGHT }}
      />
    </div>
  );
};

const initialEvent = {
  salesforceEventId: '',
  sendDataToSF: false,
  name: '',
  status: 'Draft',
  statusMessage: '',
  description: '',
  startDate: '',
  endDate: '',
  location: '',
  address: '',
  linkToEventSite: '',
  cancellationPolicy: '',
  termsAndConditions: '',
  policyLink: '',
  rejectionMessage: '',
  eventAdminContactEmail: '',
  badgeTemplateName: '',
  layoutId: '',
  freeAttendeesPerBooth: 3,
  additionalAttendeePrice: '',
  shoppingCartWait: '',
  paymentWait: '',
  packagedFoodVendorMessage: '',
  packagedFoodVendorMessageDisplay: false,
  // New fields: whether to display a generic user message and the message content
  displayUserMessage: false,
  userMessage: '',
  applicationAckMessage: '',
  refundPercentage: '',
  refundAmount: '',
  applyRefundFeePerBooth: false,
  allowApplications: true,
  allowRegistration: true,
  checkinScheduleFlag: 'required',
  requiredItemsFlag: 'required',
  approvalEmailText: '',
  checkinSchedule: [],
  requiredItems: [],
  consentMessage: '',
  consentFontSize: CONSENT_FONT_SIZE,
  eventAdmins: [],
  organizers: [],
  bazaarPaymentInfo: { provider: '', baseUrl: '', clientId: '', secretId: '', paymentConfigId: '', webhookSecret: '' },
};

const Events = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [items, setItems] = useState([]);
  const [evt, setEvt] = useState(initialEvent);
  const [layouts, setLayouts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profilesMap, setProfilesMap] = useState({});
  const [organizerProfiles, setOrganizerProfiles] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [mode, setMode] = useState('idle'); // 'idle' | 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  // Track original layout id for the currently edited event so we only enforce
  // uniqueness if the layout is actually being changed.
  const [originalLayoutId, setOriginalLayoutId] = useState('');
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const publishedLayouts = React.useMemo(() => (layouts || []).filter(l => (l?.status || '').toLowerCase() === 'published'), [layouts]);
  // Toggle to optionally hide archived events
  const [hideArchived, setHideArchived] = useState(false);
  const visibleItems = React.useMemo(() => {
    const all = items || [];
    if (!hideArchived) return all;
    return all.filter(ev => (ev?.status || '').toLowerCase() !== 'archived');
  }, [items, hideArchived]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const all = await listEvents();
        // If current user is EventAdmin, filter to events where they're listed
        if (currentUser?.uid) {
          try {
            const prof = await getUserProfile(currentUser.uid);
            if (prof?.userType === 'EventAdmin') {
              const filtered = (all || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.includes(currentUser.uid));
              setItems(filtered);
              return;
            }
          } catch (roleErr) {
            // ignore and fall back to all
          }
        }
        setItems(all);
      } catch (e) {
        console.error('Events load failed:', e);
        setError(`${e?.code || 'error'}: ${e?.message || 'Failed to load events'}`);
      }
    };
    loadEvents();
  }, []);

  // Load only EventAdmin profiles so the dropdown remains small
  useEffect(() => {
    (async () => {
      try {
        const admins = await listProfilesByType('EventAdmin');
        setProfiles(admins || []);
      } catch (e) {
        console.warn('Failed to load EventAdmin profiles for selector:', e);
        setProfiles([]);
      }
    })();
  }, []);

  // Load Organizer profiles for the Organizers selector
  useEffect(() => {
    (async () => {
      try {
        const orgs = await listProfilesByType('Organizer');
        setOrganizerProfiles(orgs || []);
      } catch (e) {
        console.warn('Failed to load Organizer profiles for selector:', e);
        setOrganizerProfiles([]);
      }
    })();
  }, []);

  // Load layouts for lookup
  useEffect(() => {
    (async () => {
      try {
        // Determine role and load appropriate layouts
        try {
          if (currentUser?.uid) {
            const prof = await getUserProfile(currentUser.uid);
            const role = prof?.userType;
            if (role === 'Admin' || role === 'Organizer') {
              const allLayouts = await listAllLayouts();
              setLayouts(allLayouts || []);
            } else if (role === 'EventAdmin') {
              const myEventLayouts = await listLayoutsByEventAdmin(currentUser.uid);
              setLayouts(myEventLayouts || []);
            } else {
              const mine = await getUserLayouts(currentUser.uid);
              setLayouts(mine || []);
            }
          } else {
            setLayouts([]);
          }
        } catch (roleErr) {
          // on error fall back to user layouts if possible
          try {
            if (currentUser?.uid) {
              const mine = await getUserLayouts(currentUser.uid);
              setLayouts(mine || []);
            } else {
              setLayouts([]);
            }
          } catch (e) {
            setLayouts([]);
          }
        }
      } catch (e) {
        console.warn('Failed to load layouts for lookup:', e?.message || e);
        setLayouts([]);
      }
    })();
  }, [currentUser?.uid]);

  const isPaymentInfoComplete = (paymentInfo) => {
    if (!paymentInfo) return false;
    const provider = (paymentInfo.provider || '').trim();
    const baseUrl = (paymentInfo.baseUrl || '').trim();
    const clientId = (paymentInfo.clientId || '').trim();
    const secretId = normalizeConfiguredValue(paymentInfo.secretId);
    const paymentConfigId = normalizeConfiguredValue(paymentInfo.paymentConfigId);
    const webhookSecret = normalizeConfiguredValue(paymentInfo.webhookSecret);
    // All fields are required
    return provider && baseUrl && clientId && secretId && paymentConfigId && webhookSecret;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEvt(prev => {
      // Confirm when moving event to Open (visible to users)
      if (name === 'status' && value === 'Open' && prev.status !== 'Open') {
        const ok = window.confirm('Changing status to Open will make the event visible to users on the website. Proceed?');
        if (!ok) return prev;
      }
      // Validate payment info when enabling registration
      if (name === 'allowRegistration' && checked) {
        const bazaarInfoComplete = isPaymentInfoComplete(prev.bazaarPaymentInfo);
        
        if (!bazaarInfoComplete) {
          setError('Cannot enable registration. The payment info section must have all required fields filled (Provider, Base URL, Client ID, Secret ID, Payment Config ID, and Webhook Secret). If a field is not needed, enter "NA".');
          return prev;
        }
      }
      return { ...prev, [name]: type === 'checkbox' ? checked : value };
    });
  };

  const handleCheckinScheduleChange = (index, field, value) => {
    setEvt(prev => {
      const next = Array.isArray(prev.checkinSchedule) ? [...prev.checkinSchedule] : [];
      next[index] = { ...(next[index] || { sequence: '', date: '', time: '', message: '' }), [field]: value };
      return { ...prev, checkinSchedule: next };
    });
  };

  const addCheckinScheduleRow = () => {
    setEvt(prev => {
      const next = Array.isArray(prev.checkinSchedule) ? [...prev.checkinSchedule] : [];
      next.push({ sequence: next.length + 1, date: '', time: '', message: '' });
      return { ...prev, checkinSchedule: next };
    });
  };

  const removeCheckinScheduleRow = (index) => {
    setEvt(prev => {
      const next = Array.isArray(prev.checkinSchedule) ? prev.checkinSchedule.filter((_, i) => i !== index) : [];
      return { ...prev, checkinSchedule: next };
    });
  };

  const handleRequiredItemChange = (index, field, value) => {
    setEvt(prev => {
      const next = Array.isArray(prev.requiredItems) ? [...prev.requiredItems] : [];
      next[index] = { ...(next[index] || { sequence: '', name: '', message: '' }), [field]: value };
      return { ...prev, requiredItems: next };
    });
  };

  const addRequiredItemRow = () => {
    setEvt(prev => {
      const next = Array.isArray(prev.requiredItems) ? [...prev.requiredItems] : [];
      next.push({ sequence: next.length + 1, name: '', message: '' });
      return { ...prev, requiredItems: next };
    });
  };

  const removeRequiredItemRow = (index) => {
    setEvt(prev => {
      const next = Array.isArray(prev.requiredItems) ? prev.requiredItems.filter((_, i) => i !== index) : [];
      return { ...prev, requiredItems: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');
    if (!evt.name?.trim()) {
      setError('Event Name is required');
      return;
    }
    const shoppingCartWait = Number.parseInt(evt.shoppingCartWait, 10);
    const paymentWait = Number.parseInt(evt.paymentWait, 10);
    if (!Number.isFinite(shoppingCartWait) || shoppingCartWait <= 0) {
      setError('Shopping Cart Wait must be a positive number of minutes.');
      return;
    }
    if (!Number.isFinite(paymentWait) || paymentWait <= 0) {
      setError('Payment Wait must be a positive number of minutes.');
      return;
    }
    // Layout uniqueness: only enforce if the layoutId is actually changing (or on create)
    const layoutIdTrimmed = (evt.layoutId || '').trim();
    const isLayoutChanging = (mode !== 'edit') || (layoutIdTrimmed !== (originalLayoutId || '').trim());
    if (layoutIdTrimmed && isLayoutChanging) {
      const othersWithLayout = items.filter(x => x.layoutId === layoutIdTrimmed && !(mode === 'edit' && x.id === editingId));
      if (othersWithLayout.length > 0) {
        setError('Selected Layout is already linked to another event. Create a copy of the layout or unlink it from that event first.');
        return;
      }
    }
    // Uniqueness validation for Event Name only
    const nameNorm = (evt.name || '').trim().toLowerCase();
    const others = items.filter(x => !(mode === 'edit' && x.id === editingId));
    if (nameNorm && others.some(x => ((x.name || '').trim().toLowerCase() === nameNorm))) {
      setError('Event Name must be unique. Another event already uses the same name.');
      return;
    }
    // If a payment provider is selected, require clientId and secretId.
    const bProv = evt.bazaarPaymentInfo && (evt.bazaarPaymentInfo.provider || '').trim();
    const bClientId = evt.bazaarPaymentInfo && (evt.bazaarPaymentInfo.clientId || '').trim();
    const bSecretId = evt.bazaarPaymentInfo ? normalizeConfiguredValue(evt.bazaarPaymentInfo.secretId) : '';
    if (bProv) {
      if (!bClientId || !bSecretId) {
        setError('Client ID and Secret ID are required when a Bazaar payment provider is selected.');
        return;
      }
    }
    // Layout ID not required by spec; it's optional
      try {
        setSaving(true);
        // Convert AdditionalAttendeePrice to cents before saving
        const evtToSave = {
          ...evt,
          shoppingCartWait,
          paymentWait,
          additionalAttendeePrice: evt.additionalAttendeePrice ? Math.round(Number(evt.additionalAttendeePrice) * 100) : 0,
          checkinSchedule: Array.isArray(evt.checkinSchedule)
            ? evt.checkinSchedule
              .map((row, index) => ({
                sequence: Number.parseInt(row?.sequence, 10) || (index + 1),
                date: row?.date || '',
                time: row?.time || '',
                message: row?.message || ''
              }))
              .filter(row => row.date || row.time || row.message)
            : [],
          requiredItems: Array.isArray(evt.requiredItems)
            ? evt.requiredItems
              .map((row, index) => ({
                sequence: Number.parseInt(row?.sequence, 10) || (index + 1),
                name: row?.name || '',
                message: row?.message || ''
              }))
              .filter(row => row.name || row.message)
            : [],
          consentMessage: normalizeConsentMessageHtml(evt.consentMessage || '', evt.consentFontSize || CONSENT_FONT_SIZE),
          consentFontSize: evt.consentFontSize || CONSENT_FONT_SIZE,
          eventAdmins: Array.isArray(evt.eventAdmins) ? [...evt.eventAdmins] : [],
          organizers: Array.isArray(evt.organizers) ? [...evt.organizers] : []
        };
        // Ensure creator is included when creating a new event
        if (mode !== 'edit' && currentUser?.uid && !evtToSave.eventAdmins.includes(currentUser.uid)) {
          evtToSave.eventAdmins.unshift(currentUser.uid);
        }
        // Attach audit UIDs so service will persist them
        if (currentUser?.uid) {
          if (mode === 'edit') {
            evtToSave.updatedByUid = currentUser.uid;
          } else {
            evtToSave.createdByUid = currentUser.uid;
            evtToSave.updatedByUid = currentUser.uid;
          }
        }
        if (mode === 'edit' && editingId) {
          const updated = await updateEvent(editingId, evtToSave);
          setSuccess(`Event updated: ${updated.name || evtToSave.name}`);
        } else {
          const created = await addEvent(evtToSave);
          setSuccess(`Event created: ${created.name}`);
        }
      setEvt(initialEvent);
      setMode('idle');
      setEditingId(null);
      setOriginalLayoutId('');
      const all = await listEvents();
      try {
        const prof = await getUserProfile(currentUser.uid);
        if (prof?.userType === 'EventAdmin') {
          const filtered = (all || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.includes(currentUser.uid));
          setItems(filtered);
        } else {
          setItems(all);
        }
      } catch (e) {
        setItems(all);
      }
    } catch (e) {
      console.error('Event create failed:', e);
      setError(`${e?.code || 'error'}: ${e?.message || (mode === 'edit' ? 'Failed to update event' : 'Failed to create event')}`);
    } finally {
      setSaving(false);
    }
  };

  const beginCreate = () => {
    setError(null);
    setSuccess('');
    setEvt({ ...initialEvent, eventAdmins: currentUser?.uid ? [currentUser.uid] : [] });
    setMode('create');
    setEditingId(null);
    setOriginalLayoutId('');
  };

  const beginEdit = (ev) => {
    setError(null);
    setSuccess('');
    setMode('edit');
    setEditingId(ev.id);
    setOriginalLayoutId(ev.layoutId || '');
    setEvt({
      salesforceEventId: ev.salesforceEventId || '',
      sendDataToSF: ev.sendDataToSF === true,
      name: ev.name || '',
      status: ev.status || 'Draft',
      statusMessage: ev.statusMessage || '',
      description: ev.description || '',
      startDate: ev.startDate || '',
      endDate: ev.endDate || '',
      location: ev.location || '',
      address: ev.address || '',
      linkToEventSite: ev.linkToEventSite || '',
      cancellationPolicy: ev.cancellationPolicy || '',
      termsAndConditions: ev.termsAndConditions || '',
      policyLink: ev.policyLink || '',
      rejectionMessage: ev.rejectionMessage || '',
      eventAdminContactEmail: ev.eventAdminContactEmail || '',
      badgeTemplateName: ev.badgeTemplateName || '',
      layoutId: ev.layoutId || '',
      freeAttendeesPerBooth: ev.freeAttendeesPerBooth ?? 3,
      additionalAttendeePrice: (typeof ev.additionalAttendeePrice === 'number' && !isNaN(ev.additionalAttendeePrice)) ? (ev.additionalAttendeePrice / 100).toString() : (ev.additionalAttendeePrice ?? ''),
      shoppingCartWait: ev.shoppingCartWait ?? ev.ShoppingCartWait ?? '',
      paymentWait: ev.paymentWait ?? ev.PaymentWait ?? '',
      packagedFoodVendorMessage: ev.packagedFoodVendorMessage || '',
      packagedFoodVendorMessageDisplay: ev.packagedFoodVendorMessageDisplay ?? false,
      displayUserMessage: ev.displayUserMessage ?? false,
      userMessage: ev.userMessage || '',
      applicationAckMessage: ev.applicationAckMessage || '',
      refundPercentage: ev.refundPercentage ?? '',
      refundAmount: ev.refundAmount ?? '',
      applyRefundFeePerBooth: ev.applyRefundFeePerBooth ?? false,
      allowApplications: ev.allowApplications ?? true,
      allowRegistration: ev.allowRegistration ?? true,
      checkinScheduleFlag: normalizeEventFormFlag(ev.checkinScheduleFlag),
      requiredItemsFlag: normalizeEventFormFlag(ev.requiredItemsFlag),
      approvalEmailText: ev.approvalEmailText || '',
      checkinSchedule: Array.isArray(ev.checkinSchedule)
        ? ev.checkinSchedule.map((row, index) => ({
          sequence: row?.sequence ?? (index + 1),
          date: row?.date || '',
          time: row?.time || '',
          message: row?.message || ''
        }))
        : [],
      requiredItems: Array.isArray(ev.requiredItems)
        ? ev.requiredItems.map((row, index) => ({
          sequence: row?.sequence ?? (index + 1),
          name: row?.name || '',
          message: row?.message || ''
        }))
        : [],
      consentMessage: ev.consentMessage || '',
      consentFontSize: ev.consentFontSize || CONSENT_FONT_SIZE,
      eventAdmins: ev.eventAdmins || [],
      organizers: ev.organizers || [],
      bazaarPaymentInfo: ev.bazaarPaymentInfo || { provider: '', baseUrl: '', clientId: '', secretId: '', paymentConfigId: '', webhookSecret: '' },
    });
  };

  const beginDuplicate = (ev) => {
    // Confirm duplication
    const ok = window.confirm(`Create a duplicate of "${ev.name}"?`);
    if (!ok) return;

    setError(null);
    setSuccess('');
    setMode('create');
    setEditingId(null);
    setOriginalLayoutId('');
    setEvt({
      salesforceEventId: '', // clear to force new
      sendDataToSF: ev.sendDataToSF === true,
      name: ev.name ? `${ev.name} (Copy)` : '',
      status: 'Draft',
      statusMessage: ev.statusMessage || '',
      description: ev.description || '',
      startDate: ev.startDate || '',
      endDate: ev.endDate || '',
      location: ev.location || '',
      address: ev.address || '',
      linkToEventSite: ev.linkToEventSite || '',
      cancellationPolicy: ev.cancellationPolicy || '',
      termsAndConditions: ev.termsAndConditions || '',
      policyLink: ev.policyLink || '',
      rejectionMessage: ev.rejectionMessage || '',
      eventAdminContactEmail: ev.eventAdminContactEmail || '',
      badgeTemplateName: ev.badgeTemplateName || '',
      layoutId: ev.layoutId || '',
      freeAttendeesPerBooth: ev.freeAttendeesPerBooth ?? 3,
      additionalAttendeePrice: (typeof ev.additionalAttendeePrice === 'number' && !isNaN(ev.additionalAttendeePrice)) ? (ev.additionalAttendeePrice / 100).toString() : (ev.additionalAttendeePrice ?? ''),
      shoppingCartWait: ev.shoppingCartWait ?? ev.ShoppingCartWait ?? '',
      paymentWait: ev.paymentWait ?? ev.PaymentWait ?? '',
      packagedFoodVendorMessage: ev.packagedFoodVendorMessage || '',
      packagedFoodVendorMessageDisplay: ev.packagedFoodVendorMessageDisplay ?? false,
      displayUserMessage: ev.displayUserMessage ?? false,
      userMessage: ev.userMessage || '',
      applicationAckMessage: ev.applicationAckMessage || '',
      refundPercentage: ev.refundPercentage ?? '',
      refundAmount: ev.refundAmount ?? '',
      applyRefundFeePerBooth: ev.applyRefundFeePerBooth ?? false,
      allowApplications: ev.allowApplications ?? true,
      allowRegistration: ev.allowRegistration ?? true,
      checkinScheduleFlag: normalizeEventFormFlag(ev.checkinScheduleFlag),
      requiredItemsFlag: normalizeEventFormFlag(ev.requiredItemsFlag),
      approvalEmailText: ev.approvalEmailText || '',
      checkinSchedule: Array.isArray(ev.checkinSchedule)
        ? ev.checkinSchedule.map((row, index) => ({
          sequence: row?.sequence ?? (index + 1),
          date: row?.date || '',
          time: row?.time || '',
          message: row?.message || ''
        }))
        : [],
      requiredItems: Array.isArray(ev.requiredItems)
        ? ev.requiredItems.map((row, index) => ({
          sequence: row?.sequence ?? (index + 1),
          name: row?.name || '',
          message: row?.message || ''
        }))
        : [],
      consentMessage: ev.consentMessage || '',
      consentFontSize: ev.consentFontSize || CONSENT_FONT_SIZE,
      eventAdmins: ev.eventAdmins || [],
      organizers: ev.organizers || [],
      bazaarPaymentInfo: ev.bazaarPaymentInfo || { provider: '', baseUrl: '', clientId: '', secretId: '', paymentConfigId: '', webhookSecret: '' },
    });
    // Optionally scroll to form
    try { document.querySelector('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
  };

  const handleDelete = async (ev) => {
    setError(null);
    setSuccess('');
    const ok = window.confirm(`Delete event "${ev.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteEvent(ev.id);
      const all = await listEvents();
      try {
        const prof = await getUserProfile(currentUser.uid);
        if (prof?.userType === 'EventAdmin') {
          const filtered = (all || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.includes(currentUser.uid));
          setItems(filtered);
        } else {
          setItems(all);
        }
      } catch (e) {
        setItems(all);
      }
      setSuccess('Event deleted.');
      // If currently editing this event, reset form
      if (mode !== 'idle' && editingId === ev.id) {
        setMode('idle');
        setEditingId(null);
        setEvt(initialEvent);
      }
    } catch (e) {
      console.error('Failed to delete event:', e);
      setError(`${e?.code || 'error'}: ${e?.message || 'Failed to delete event'}`);
    }
  };

  const openBazaar = async (ev) => {
    try {
      if (!ev?.layoutId) {
        alert('No layout linked to this event.');
        return;
      }
      const layout = await getLayoutPublic(ev.layoutId);
      if (!layout) {
        alert('Layout not found for this event.');
        return;
      }
      if ((layout.status || '').toLowerCase() !== 'published') {
        alert('Registration is not available yet. Please check back when the layout is published.');
        return;
      }
      navigate('/booth-selection', { state: { existingLayout: layout, event: ev, backTo: '/events' } });
    } catch (e) {
      console.error('Failed to open booth selection:', e);
      alert('Unable to open booth selection for this event. Please try again later.');
    }
  };

  const cancelEdit = () => {
    setMode('idle');
    setEditingId(null);
    setEvt(initialEvent);
  };

  // Helpers for date formatting and status badge color (match LayoutAdmin styles)
  const toMillis = (ts) => {
    try {
      if (!ts) return null;
      if (typeof ts.toMillis === 'function') return ts.toMillis();
      if (ts.seconds) return ts.seconds * 1000;
      if (typeof ts === 'string' || typeof ts === 'number' || ts instanceof Date) {
        const d = new Date(ts);
        const ms = d.getTime();
        return isNaN(ms) ? null : ms;
      }
    } catch {}
    return null;
  };
  const formatDate = (ts, withTime = false) => {
    const ms = toMillis(ts);
    if (ms == null) return '-';
    try {
      const d = new Date(ms);
      return withTime ? d.toLocaleString() : d.toLocaleDateString();
    } catch {
      return '-';
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#f39c12';
      case 'Open': return '#27ae60';
      case 'Closed': return '#95a5a6';
      case 'Archived': return '#95a5a6';
      default: return '#f39c12';
    }
  };

  // Helper to resolve profile name with a simple cache (fetches missing profiles)
  const getProfileName = (uid) => {
    if (!uid) return '-';
    const p = profilesMap[uid];
    if (p) {
      const name = `${(p.firstName || '').trim()} ${(p.lastName || '').trim()}`.trim();
      return name || p.email || uid;
    }
    // if not cached, return the uid for now and fetch in background
    (async () => {
      try {
        const fetched = await getUserProfile(uid);
        if (fetched && fetched.id) setProfilesMap(prev => ({ ...prev, [uid]: fetched }));
      } catch (e) {}
    })();
    return uid;
  };

  // Preload creator/updater profiles for visible items
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uids = new Set();
        (visibleItems || []).forEach(ev => {
          if (ev?.createdByUid) uids.add(ev.createdByUid);
          if (ev?.createdBy) uids.add(ev.createdBy);
          if (ev?.userId) uids.add(ev.userId);
          if (ev?.updatedByUid) uids.add(ev.updatedByUid);
          if (ev?.updatedBy) uids.add(ev.updatedBy);
        });
        const missing = Array.from(uids).filter(uid => uid && !profilesMap[uid]);
        if (missing.length === 0) return;
        const results = await Promise.all(missing.map(uid => getUserProfile(uid).catch(() => null)));
        if (cancelled) return;
        const map = {};
        results.forEach((p, i) => { if (p && p.id) map[missing[i]] = p; });
        if (Object.keys(map).length) setProfilesMap(prev => ({ ...prev, ...map }));
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [visibleItems]);

  return (
    <div className="profile-container profile-wide-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="apply-btn" onClick={beginCreate} style={{ marginRight: 8 }}>+ Add New Event</button>
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">Events</h2>
  <div className="profile-hint" style={{ marginBottom: 8 }}>Project: {projectId || 'unknown'}</div>
      {error && <div className="profile-error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="profile-hint" style={{ marginBottom: 12, color: '#2e7d32' }}>{success}</div>}

      {/* Existing Events at the top */}
      <h3 className="profile-title" style={{ marginTop: 0 }}>Existing Events</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={hideArchived}
            onChange={e => setHideArchived(e.target.checked)}
          />
          Hide Archived
        </label>
        <div style={{ fontSize: 12, color: '#555' }}>Showing {visibleItems.length} of {(items || []).length} events</div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="events-table layouts-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Actions</th>
              <th>Name</th>
              <th>ID</th>
              <th>Status</th>
              <th>Location</th>
              <th>Created</th>
              <th>Created By</th>
              <th>Last Updated</th>
              <th>Updated By</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(ev => {
              const layout = layouts.find(l => l.id === ev.layoutId);
              const layoutName = layout ? (layout.name || layout.title || ev.layoutId) : (ev.layoutId || '-');
              return (
                <tr key={ev.id}>
                  <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                    <div className="icon-actions">
                      <button type="button" className="icon-btn bazaar" aria-label="Bazaar" data-tooltip="Bazaar" onClick={() => openBazaar(ev)}>🛍️</button>
                      <button type="button" className="icon-btn edit" aria-label="Edit" data-tooltip="Edit" onClick={() => beginEdit(ev)}>✏️</button>
                      <button type="button" className="icon-btn duplicate" aria-label="Duplicate" data-tooltip="Duplicate" onClick={() => beginDuplicate(ev)}>📄</button>
                      <button type="button" className="icon-btn delete" aria-label="Delete" data-tooltip="Delete" onClick={() => handleDelete(ev)}>🗑️</button>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontWeight: 600, color: '#213547' }}>{ev.name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: '#666' }}>{ev.id}</td>
                  <td>
                    <span className="layout-status-badge" style={{ backgroundColor: getStatusColor(ev.status), padding: '0.15rem 0.6rem', borderRadius: 8 }}>
                      {ev.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: '#666' }}>{ev.location}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>{formatDate(ev.createdAt, true)}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>{getProfileName(ev.createdByUid || ev.createdBy || ev.userId)}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>{formatDate(ev.updatedAt, true)}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>{getProfileName(ev.updatedByUid || ev.updatedBy || ev.userId)}</td>
                </tr>
              );
            })}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>No events</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      

      {/* Top action bar: Save/Cancel when creating or editing */}
      {(mode === 'create' || mode === 'edit') && (
        <div className="profile-actions" style={{ marginTop: 12, marginBottom: 8 }}>
          <button type="submit" form="eventsForm" className="apply-btn" disabled={saving}>
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save Changes' : 'Save Event')}
          </button>
          <button type="button" className="cancel-btn" onClick={cancelEdit} disabled={saving}>Cancel</button>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
      <form id="eventsForm" onSubmit={handleSubmit}>
        <div className="profile-grid">
          <div>
            <label className="profile-label">Salesforce Event ID</label>
            <input name="salesforceEventId" value={evt.salesforceEventId} onChange={handleChange} className="profile-input" placeholder="e.g. a21TQ000000KB5lYAG" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 28 }}>
            <input
              type="checkbox"
              id="sendDataToSF"
              name="sendDataToSF"
              checked={!!evt.sendDataToSF}
              onChange={handleChange}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <label htmlFor="sendDataToSF" style={{ margin: 0, cursor: 'pointer', fontWeight: 500 }}>Send Data to SF</label>
          </div>
          <div>
            <label className="profile-label">Event Name</label>
            <input name="name" value={evt.name} onChange={handleChange} className="profile-input" placeholder="Event Name" />
          </div>
          <div>
            <label className="profile-label">Status</label>
            <select name="status" value={evt.status} onChange={handleChange} className="profile-input">
              <option value="Draft">Draft</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="profile-label">Layout ID</label>
            <select name="layoutId" value={evt.layoutId} onChange={handleChange} className="profile-input">
              <option value="">-- Select Layout --</option>
              {publishedLayouts.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name || l.title || l.id} {l.status ? `(${l.status})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="profile-label">Status Message</label>
            <input name="statusMessage" value={evt.statusMessage} onChange={handleChange} className="profile-input" placeholder="Registration is open..." />
          </div>
          <div>
            <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input 
                type="checkbox" 
                name="allowApplications" 
                checked={evt.allowApplications ?? true} 
                onChange={handleChange} 
                style={{ width: 'auto' }}
              />
              Allow Applications
            </label>
          </div>
          <div>
            <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input 
                type="checkbox" 
                name="allowRegistration" 
                checked={evt.allowRegistration ?? true} 
                onChange={handleChange} 
                style={{ width: 'auto' }}
              />
              Allow Registration
            </label>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Approval Email Text</label>
            <textarea
              name="approvalEmailText"
              value={evt.approvalEmailText}
              onChange={handleChange}
              className="profile-input"
              rows={4}
              placeholder="Text to include in application approval emails"
            />
          </div>
          <div>
            <label className="profile-label">Free Attendees per Booth</label>
            <input type="number" min="0" step="1" name="freeAttendeesPerBooth" value={evt.freeAttendeesPerBooth} onChange={handleChange} className="profile-input" placeholder="3" />
          </div>
          <div>
            <label className="profile-label">Additional Attendee Price</label>
            <input type="number" min="0" step="0.01" name="additionalAttendeePrice" value={evt.additionalAttendeePrice} onChange={handleChange} className="profile-input" placeholder="0.00" />
          </div>
          <div>
            <label className="profile-label">Shopping Cart Wait (minutes)</label>
            <input type="number" min="1" step="1" name="shoppingCartWait" value={evt.shoppingCartWait} onChange={handleChange} className="profile-input" placeholder="Required" required />
          </div>
          <div>
            <label className="profile-label">Payment Wait (minutes)</label>
            <input type="number" min="1" step="1" name="paymentWait" value={evt.paymentWait} onChange={handleChange} className="profile-input" placeholder="Required" required />
          </div>
          
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Description</label>
            <textarea name="description" value={evt.description} onChange={handleChange} className="profile-input" rows={3} placeholder="Event description" />
          </div>
          <div>
            <label className="profile-label">Start Date</label>
            <input name="startDate" value={evt.startDate} onChange={handleChange} className="profile-input" placeholder="May 24, 2025" />
          </div>
          <div>
            <label className="profile-label">End Date</label>
            <input name="endDate" value={evt.endDate} onChange={handleChange} className="profile-input" placeholder="May 26, 2025" />
          </div>
          <div>
            <label className="profile-label">Location</label>
            <input name="location" value={evt.location} onChange={handleChange} className="profile-input" placeholder="Venue name" />
          </div>
          <div>
            <label className="profile-label">Address</label>
            <input name="address" value={evt.address} onChange={handleChange} className="profile-input" placeholder="Street, City, State Zip" />
          </div>
          <div>
            <label className="profile-label">Link to Event Site</label>
            <input name="linkToEventSite" value={evt.linkToEventSite} onChange={handleChange} className="profile-input" placeholder="https://..." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Cancellation Policy</label>
            <textarea name="cancellationPolicy" value={evt.cancellationPolicy} onChange={handleChange} className="profile-input" rows={2} placeholder="Cancellation info" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Terms & Conditions</label>
            <textarea name="termsAndConditions" value={evt.termsAndConditions} onChange={handleChange} className="profile-input" rows={2} placeholder="Terms and conditions" />
          </div>
          <div>
            <label className="profile-label">Policy Link</label>
            <input name="policyLink" value={evt.policyLink} onChange={handleChange} className="profile-input" placeholder="https://..." />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Rejection Message</label>
            <textarea
              name="rejectionMessage"
              value={evt.rejectionMessage}
              onChange={handleChange}
              className="profile-input"
              rows={3}
              placeholder="Message shown when an application is rejected"
            />
          </div>
          <div>
            <label className="profile-label">EventAdmin Contact Email</label>
            <input
              type="email"
              name="eventAdminContactEmail"
              value={evt.eventAdminContactEmail}
              onChange={handleChange}
              className="profile-input"
              placeholder="name@example.com"
            />
          </div>
          <div>
            <label className="profile-label">Badge Template Name</label>
            <input
              name="badgeTemplateName"
              value={evt.badgeTemplateName}
              onChange={handleChange}
              className="profile-input"
              placeholder="vendor-badge-template.zpl"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Packaged-Food Vendor Message</label>
            <textarea name="packagedFoodVendorMessage" value={evt.packagedFoodVendorMessage} onChange={handleChange} className="profile-input" rows={3} placeholder="Message to display for packaged-food vendors" />
          </div>
          <div>
            <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input 
                type="checkbox" 
                name="packagedFoodVendorMessageDisplay" 
                checked={evt.packagedFoodVendorMessageDisplay} 
                onChange={handleChange} 
                style={{ width: 'auto' }}
              />
              Display Packaged-Food Vendor Message
            </label>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Application Acknowledgment Message</label>
            <textarea name="applicationAckMessage" value={evt.applicationAckMessage} onChange={handleChange} className="profile-input" rows={3} placeholder="Message to display when application is submitted" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ marginBottom: 8, maxWidth: 320 }}>
              <label className="profile-label">CheckInScheduleFlag</label>
              <select
                name="checkinScheduleFlag"
                value={normalizeEventFormFlag(evt.checkinScheduleFlag)}
                onChange={handleChange}
                className="profile-input"
              >
                {EVENT_FORM_FLAG_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <label className="profile-label">Check-In Schedule</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(evt.checkinSchedule || []).map((row, index) => (
                <div key={`checkin-${index}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 2fr auto', gap: 8, alignItems: 'start' }}>
                  <input
                    className="profile-input"
                    placeholder="Sequence"
                    type="number"
                    min="1"
                    value={row.sequence ?? ''}
                    onChange={e => handleCheckinScheduleChange(index, 'sequence', e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Date"
                    value={row.date || ''}
                    onChange={e => handleCheckinScheduleChange(index, 'date', e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Time"
                    value={row.time || ''}
                    onChange={e => handleCheckinScheduleChange(index, 'time', e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Message"
                    value={row.message || ''}
                    onChange={e => handleCheckinScheduleChange(index, 'message', e.target.value)}
                  />
                  <button type="button" className="cancel-btn" onClick={() => removeCheckinScheduleRow(index)}>Remove</button>
                </div>
              ))}
              <div>
                <button type="button" className="apply-btn" onClick={addCheckinScheduleRow}>+ Add Check-In Schedule</button>
              </div>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ marginBottom: 8, maxWidth: 320 }}>
              <label className="profile-label">RequiredItemsFlag</label>
              <select
                name="requiredItemsFlag"
                value={normalizeEventFormFlag(evt.requiredItemsFlag)}
                onChange={handleChange}
                className="profile-input"
              >
                {EVENT_FORM_FLAG_OPTIONS.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
            <label className="profile-label">Required Items</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(evt.requiredItems || []).map((row, index) => (
                <div key={`req-${index}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 2fr auto', gap: 8, alignItems: 'start' }}>
                  <input
                    className="profile-input"
                    placeholder="Sequence"
                    type="number"
                    min="1"
                    value={row.sequence ?? ''}
                    onChange={e => handleRequiredItemChange(index, 'sequence', e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Name"
                    value={row.name || ''}
                    onChange={e => handleRequiredItemChange(index, 'name', e.target.value)}
                  />
                  <input
                    className="profile-input"
                    placeholder="Message"
                    value={row.message || ''}
                    onChange={e => handleRequiredItemChange(index, 'message', e.target.value)}
                  />
                  <button type="button" className="cancel-btn" onClick={() => removeRequiredItemRow(index)}>Remove</button>
                </div>
              ))}
              <div>
                <button type="button" className="apply-btn" onClick={addRequiredItemRow}>+ Add Required Item</button>
              </div>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Consent Message</label>
            <RichTextEditor
              value={evt.consentMessage || ''}
              onChange={(html) => setEvt(prev => ({ ...prev, consentMessage: html }))}
              fontSize={evt.consentFontSize || CONSENT_FONT_SIZE}
              onFontSizeChange={(size) => setEvt(prev => ({ ...prev, consentFontSize: size }))}
              placeholder="Enter consent details with formatting"
            />
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              Supports basic formatting (bold, italic, underline, bullets). Font family is fixed; use A- / A+ to adjust size.
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                name="displayUserMessage"
                checked={!!evt.displayUserMessage}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Display User Message (shown on public pages)
            </label>
          </div>
          {evt.displayUserMessage && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="profile-label">User Message</label>
              <textarea name="userMessage" value={evt.userMessage} onChange={handleChange} className="profile-input" rows={3} placeholder="Message to display to users (e.g., registration notes)" />
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="profile-label">Bazaar Payment Info</label>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8, fontStyle: 'italic' }}>
              Before you enable registration, ensure all payment fields are filled. If a field is not needed for your payment provider, enter "NA".
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div>
                <label className="profile-label">Provider</label>
                <select
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.provider) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), provider: e.target.value } }))}
                >
                  <option value="">-- Select Provider --</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Stripe">Stripe</option>
                </select>
              </div>
              <div>
                <label className="profile-label">Base URL</label>
                <input
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.baseUrl) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), baseUrl: e.target.value } }))}
                  placeholder="https://api.paypal.com or https://api.stripe.com"
                />
              </div>
              <div>
                <label className="profile-label">Client ID</label>
                <input
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.clientId) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), clientId: e.target.value } }))}
                  placeholder="PayPal Client ID or Stripe Publishable Key"
                />
              </div>
              <div>
                <label className="profile-label">Secret ID</label>
                <input
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.secretId) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), secretId: e.target.value } }))}
                  placeholder="PayPal Secret ID or Stripe Secret Key"
                />
              </div>
              <div>
                <label className="profile-label">Payment Config ID</label>
                <input
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.paymentConfigId) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), paymentConfigId: e.target.value } }))}
                  placeholder="Must provide to control payment methods"
                />
              </div>
              <div>
                <label className="profile-label">Webhook Secret</label>
                <input
                  className="profile-input"
                  value={(evt.bazaarPaymentInfo && evt.bazaarPaymentInfo.webhookSecret) || ''}
                  onChange={e => setEvt(prev => ({ ...prev, bazaarPaymentInfo: { ...(prev.bazaarPaymentInfo || {}), webhookSecret: e.target.value } }))}
                  placeholder="Webhook secret for payment provider"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: '0 0 180px' }}>
                <label className="profile-label">Refund Fee Percentage</label>
                <input type="number" min="0" max="100" step="0.01" name="refundPercentage" value={evt.refundPercentage} onChange={handleChange} className="profile-input" placeholder="e.g. 85.00" style={{ width: '100%' }} />
              </div>
              <div style={{ flex: '0 0 180px' }}>
                <label className="profile-label">Refund Fee Amount</label>
                <input type="number" min="0" step="0.01" name="refundAmount" value={evt.refundAmount} onChange={handleChange} className="profile-input" placeholder="e.g. 50.00" style={{ width: '100%' }} />
              </div>
              <div>
                <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input 
                    type="checkbox" 
                    name="applyRefundFeePerBooth" 
                    checked={evt.applyRefundFeePerBooth} 
                    onChange={handleChange} 
                    style={{ width: 'auto' }}
                  />
                  Apply Refund Fee Amount Per Booth
                </label>
              </div>
            </div>
          </div>
            <div style={{ marginTop: 12 }}>
              <label className="profile-label">Event Admin</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <select
                value={selectedAdmin}
                onChange={e => setSelectedAdmin(e.target.value)}
                className="profile-input"
                style={{ minWidth: 320 }}
              >
                <option value="">-- Select Event Admin --</option>
                {profiles.filter(p => p.userType === 'EventAdmin').map(p => (
                  <option key={p.id} value={p.id}>{((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || p.id}</option>
                ))}
              </select>
              <button
                type="button"
                className="apply-btn"
                onClick={() => {
                  if (!selectedAdmin) return;
                  setEvt(prev => {
                    const list = Array.isArray(prev.eventAdmins) ? [...prev.eventAdmins] : [];
                    if (!list.includes(selectedAdmin)) list.push(selectedAdmin);
                    return { ...prev, eventAdmins: list };
                  });
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                Add
              </button>
            </div>
            {Array.isArray(evt.eventAdmins) && evt.eventAdmins.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {evt.eventAdmins.map(uid => {
                  const p = profiles.find(x => x.id === uid);
                  const name = p ? ((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || uid : uid;
                  return (
                    <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 13, color: '#333' }}>{name}</div>
                      <button type="button" className="cancel-btn" onClick={() => setEvt(prev => ({ ...prev, eventAdmins: (prev.eventAdmins || []).filter(x => x !== uid) }))}>Remove</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <label className="profile-label">Event Organizers</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <select
                value={selectedOrganizer}
                onChange={e => setSelectedOrganizer(e.target.value)}
                className="profile-input"
                style={{ minWidth: 320 }}
              >
                <option value="">-- Select Event Organizer --</option>
                {organizerProfiles.map(p => (
                  <option key={p.id} value={p.id}>{((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || p.id}</option>
                ))}
              </select>
              <button
                type="button"
                className="apply-btn"
                onClick={() => {
                  if (!selectedOrganizer) return;
                  setEvt(prev => {
                    const list = Array.isArray(prev.organizers) ? [...prev.organizers] : [];
                    if (!list.includes(selectedOrganizer)) list.push(selectedOrganizer);
                    return { ...prev, organizers: list };
                  });
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                Add
              </button>
            </div>
            {Array.isArray(evt.organizers) && evt.organizers.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {evt.organizers.map(uid => {
                  const p = organizerProfiles.find(x => x.id === uid) || profiles.find(x => x.id === uid) || profilesMap[uid] || null;
                  const name = p ? ((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || uid : uid;
                  return (
                    <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 13, color: '#333' }}>{name}</div>
                      <button type="button" className="cancel-btn" onClick={() => setEvt(prev => ({ ...prev, organizers: (prev.organizers || []).filter(x => x !== uid) }))}>Remove</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </form>
      )}
    </div>
  );
};

export default Events;
